import { useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, Clock, Heart, MoreHorizontal, X, Plus, Settings, UserX
} from 'lucide-react';
import { cn } from '@/lib/utils';
import APP_ICONS, { PastelIcon } from '@/utils/appIcons';
import { haptics } from '@/utils/haptics';
import DeleteAccountDialog from './DeleteAccountDialog';

/**
 * BottomNav - iOS-style tab bar with centered floating add button
 * 
 * Layout: Home | Reminders | [+] | Network | More
 * The + button is the primary action button
 */

const MAIN_NAV_ITEMS = [
  { id: 'home', label: 'Home', icon: Home, path: '/' },
  { id: 'reminders', label: 'Reminders', icon: Clock, path: '/dates' },
  { id: 'add', label: '', icon: Plus, isAdd: true },
  { id: 'network', label: 'Network', icon: Heart, path: '/emergency-binder' },
  { id: 'more', label: 'More', icon: MoreHorizontal, isMenu: true },
];

const MORE_MENU_ITEMS = [
  { id: 'medical', label: 'Medical', icon: APP_ICONS.medicalInfo, path: '/medical' },
  { id: 'documents', label: 'Documents', icon: APP_ICONS.documents, path: '/documents' },
  { id: 'vehicles', label: 'Vehicles', icon: APP_ICONS.carMaintenance, path: '/car-maintenance' },
  { id: 'parking', label: 'Parking', icon: APP_ICONS.parking, path: '/parking' },
  { id: 'contacts', label: 'Contacts', icon: APP_ICONS.familyContacts, path: '/contacts-places' },
  { id: 'dates', label: 'Dates', icon: APP_ICONS.dates, path: '/dates' },
  { id: 'binder', label: 'Emergency Binder', icon: APP_ICONS.emergencyBinder, path: '/emergency-binder' },
  { id: 'pet', label: 'Pet Info', icon: APP_ICONS.pet, path: '/pet-info' },
  { id: 'settings', label: 'Settings', isSettings: true },
];

const ADD_MENU_ITEMS = [
  { id: 'document', label: 'Document', icon: APP_ICONS.documents, path: '/documents' },
  { id: 'parking', label: 'Parking Location', icon: APP_ICONS.parking, path: '/parking' },
  { id: 'date', label: 'Reminder', icon: APP_ICONS.dates, path: '/dates' },
  { id: 'medical', label: 'Medical Info', icon: APP_ICONS.medicalInfo, path: '/medical' },
  { id: 'important-date', label: 'Important Date', icon: APP_ICONS.dates, path: '/dates' },
  { id: 'contact', label: 'Contact', icon: APP_ICONS.familyContacts, path: '/contacts-places' },
  { id: 'car', label: 'Car Maintenance', icon: APP_ICONS.carMaintenance, path: '/car-maintenance' },
];

// Bottom Sheet Component
const BottomSheet = ({ isOpen, onClose, title, items, onItemClick }) => {
  const sheetRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragY, setDragY] = useState(0);
  const startYRef = useRef(0);

  const handleTouchStart = (e) => {
    startYRef.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startYRef.current;
    if (diff > 0) {
      setDragY(diff);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (dragY > 100) {
      onClose();
    }
    setDragY(0);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Sheet */}
      <div 
        ref={sheetRef}
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl"
        style={{ 
          transform: `translateY(${dragY}px)`,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out',
          animation: 'sheetSlideUp 0.3s ease-out',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-slate-300" />
        </div>
        
        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-3 border-b border-slate-100">
          <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
          <button 
            onClick={() => {
              haptics.tap();
              onClose();
            }}
            className="p-2 -mr-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 active:scale-90 transition-all"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Menu Items */}
        <div className="p-4 pb-6">
          <div className="grid grid-cols-4 gap-y-4 gap-x-2">
            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  haptics.select();
                  onItemClick(item);
                }}
                className="flex flex-col items-center p-2 rounded-xl active:bg-slate-100 active:scale-95 transition-all touch-target"
                data-testid={`more-menu-${item.id}`}
              >
                <div className="mb-1.5">
                  {item.icon ? (
                    <PastelIcon src={item.icon} alt={item.label} size={44} />
                  ) : item.isSettings ? (
                    <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center">
                      <Settings className="h-6 w-6 text-slate-600" />
                    </div>
                  ) : null}
                </div>
                <span className="text-[11px] font-medium text-slate-600 text-center leading-tight">
                  {item.label}
                </span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Safe area */}
        <div className="h-safe-area-bottom bg-white" />
      </div>
      
      <style>{`
        @keyframes sheetSlideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path.startsWith('/dates') || path.startsWith('/calendar')) return 'reminders';
    if (path.startsWith('/emergency-binder') || path.startsWith('/contacts-places')) return 'network';
    return 'home';
  };
  
  const activeTab = getActiveTab();
  
  const handleNavClick = (item) => {
    haptics.tap();
    
    if (item.isAdd) {
      setShowAddMenu(true);
      return;
    }
    
    if (item.isMenu) {
      setShowMoreMenu(true);
      return;
    }
    
    if (location.pathname === item.path) return;
    navigate(item.path);
  };

  const handleMenuItemClick = (item, closeMenu) => {
    // Handle special settings menu item
    if (item.isSettings) {
      closeMenu();
      setTimeout(() => setShowSettingsMenu(true), 200);
      return;
    }
    closeMenu();
    setTimeout(() => navigate(item.path), 200);
  };

  const handleSettingsItemClick = (item) => {
    setShowSettingsMenu(false);
    if (item.id === 'delete-account') {
      setTimeout(() => setShowDeleteDialog(true), 200);
    } else if (item.id === 'account') {
      setTimeout(() => navigate('/settings/account'), 200);
    } else if (item.path) {
      setTimeout(() => navigate(item.path), 200);
    }
  };

  const SETTINGS_MENU_ITEMS = [
    { id: 'account', label: 'Account Settings', path: '/settings/account' },
    { id: 'notifications', label: 'Notifications', path: '/settings/notifications' },
    { id: 'calendar', label: 'Calendar Sync', path: '/settings/calendar' },
    { id: 'security', label: 'Security & 2FA', path: '/admin/security' },
    { id: 'delete-account', label: 'Delete Account', isDanger: true },
  ];

  return (
    <>
      {/* More Menu Bottom Sheet */}
      <BottomSheet
        isOpen={showMoreMenu}
        onClose={() => setShowMoreMenu(false)}
        title="More"
        items={MORE_MENU_ITEMS}
        onItemClick={(item) => handleMenuItemClick(item, () => setShowMoreMenu(false))}
      />

      {/* Add Menu Bottom Sheet */}
      <BottomSheet
        isOpen={showAddMenu}
        onClose={() => setShowAddMenu(false)}
        title="Add New Item"
        items={ADD_MENU_ITEMS}
        onItemClick={(item) => handleMenuItemClick(item, () => setShowAddMenu(false))}
      />

      {/* Settings Bottom Sheet */}
      {showSettingsMenu && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowSettingsMenu(false)}
          />
          
          {/* Settings Sheet */}
          <div 
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl"
            style={{ animation: 'sheetSlideUp 0.3s ease-out' }}
          >
            {/* Drag Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 rounded-full bg-slate-300" />
            </div>
            
            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-slate-600" />
                <h3 className="text-lg font-semibold text-slate-800">Settings</h3>
              </div>
              <button 
                onClick={() => {
                  haptics.tap();
                  setShowSettingsMenu(false);
                }}
                className="p-2 -mr-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 active:scale-90 transition-all"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Settings Items */}
            <div className="p-4 pb-6 space-y-1">
              {SETTINGS_MENU_ITEMS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    haptics.select();
                    handleSettingsItemClick(item);
                  }}
                  className={cn(
                    "w-full flex items-center justify-between p-4 rounded-xl transition-all",
                    item.isDanger 
                      ? "text-rose-600 hover:bg-rose-50 active:bg-rose-100" 
                      : "text-slate-700 hover:bg-slate-50 active:bg-slate-100"
                  )}
                  data-testid={`mobile-settings-${item.id}`}
                >
                  <span className="font-medium">{item.label}</span>
                  {item.isDanger ? (
                    <UserX className="h-5 w-5" />
                  ) : (
                    <span className="text-slate-400">›</span>
                  )}
                </button>
              ))}
            </div>
            
            {/* Safe area */}
            <div className="h-safe-area-bottom bg-white" />
          </div>
          
          <style>{`
            @keyframes sheetSlideUp {
              from { transform: translateY(100%); }
              to { transform: translateY(0); }
            }
          `}</style>
        </div>
      )}

      {/* Delete Account Dialog */}
      <DeleteAccountDialog 
        open={showDeleteDialog} 
        onOpenChange={setShowDeleteDialog} 
      />

      {/* Bottom Navigation Bar */}
      <nav 
        className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200/50" 
        data-testid="bottom-nav"
        role="navigation"
        aria-label="Main navigation"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex items-center justify-around h-16 max-w-lg mx-auto px-2">
          {MAIN_NAV_ITEMS.map((item) => {
            const isActive = item.id === activeTab;
            const Icon = item.icon;
            
            // Special styling for the centered Add button
            if (item.isAdd) {
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  className={cn(
                    "flex items-center justify-center",
                    "w-14 h-14 -mt-5 rounded-full",
                    "bg-[#5B9BD5] shadow-lg",
                    "transition-all duration-150",
                    "active:scale-90 focus:outline-none focus:ring-2 focus:ring-[#5B9BD5] focus:ring-offset-2"
                  )}
                  data-testid="bottom-nav-add"
                  aria-label="Add new item"
                >
                  <Plus className="h-7 w-7 text-white" strokeWidth={2.5} aria-hidden="true" />
                </button>
              );
            }
            
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className={cn(
                  "flex flex-col items-center justify-center",
                  "w-14 h-14 rounded-xl touch-target",
                  "transition-all duration-150",
                  "active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#5B9BD5]/50"
                )}
                data-testid={`bottom-nav-${item.id}`}
                aria-label={item.label}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className={cn(
                  "h-6 w-6 mb-0.5 transition-colors",
                  isActive ? "text-[#5B9BD5]" : "text-slate-400"
                )} aria-hidden="true" />
                <span className={cn(
                  "text-[10px] font-medium transition-colors",
                  isActive ? "text-[#5B9BD5]" : "text-slate-400"
                )}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default BottomNav;
